import { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Text } from '@frontend/design-system';

const styles = {
  wrapper: css`
    display: flex;
    gap: var(--fabric-space-gap-xs);
    align-items: center;
  `,
  syncedAtText: css`
    white-space: nowrap;
    color: var(--fabric-color-neutral-70);
  `,
  syncedAtIcon: css`
    color: var(--fabric-color-neutral-70);
    flex-shrink: 0;
  `,
};
const timePeriodTranslations = {
  days: (period: number) => i18next.t('Synced {{period}}d ago', { ns: 'payments', period }),
  hours: (period: number) => i18next.t('Synced {{period}}h ago', { ns: 'payments', period }),
  minutes: (period: number) => i18next.t('Synced {{period}}m ago', { ns: 'payments', period }),
  seconds: (period: number) => i18next.t('Synced {{period}}s ago', { ns: 'payments', period }),
};

export const getPeriodTimeAgo = (
  prevDate: string
): {
  value: number;
  unit: 'days' | 'hours' | 'minutes' | 'seconds';
} => {
  const now = dayjs();
  const prev = dayjs(prevDate);

  const days = now.diff(prev, 'day');
  if (days >= 1) {
    return {
      value: days,
      unit: 'days',
    };
  }

  const hours = now.diff(prev, 'hour');
  if (hours >= 1) {
    return {
      value: hours,
      unit: 'hours',
    };
  }

  const minutes = now.diff(prev, 'minute');
  if (minutes >= 1) {
    return {
      value: minutes,
      unit: 'minutes',
    };
  }

  const seconds = now.diff(prev, 'second');
  return {
    value: seconds,
    unit: 'seconds',
  };
};

export type SyncedAtTextProps = {
  lastSyncCompletedAt?: string;
};
export const SyncedAtText = ({ lastSyncCompletedAt }: SyncedAtTextProps) => {
  const { t } = useTranslation('payments');

  const getSyncedPeriod = () => {
    if (!lastSyncCompletedAt) {
      return t('Not synced yet');
    }

    const { value, unit } = getPeriodTimeAgo(lastSyncCompletedAt);

    if (value < 1) {
      return t('Synced just now');
    }

    return timePeriodTranslations[unit](value);
  };

  const [syncedPeriod, setSyncedPeriod] = useState(getSyncedPeriod());

  useEffect(() => {
    if (!lastSyncCompletedAt) return;

    const { unit } = getPeriodTimeAgo(lastSyncCompletedAt);

    const timeoutMap = {
      seconds: 1000,
      minutes: 60000,
      hours: 3600000,
      days: 86400000,
    };
    const intervalTimeout = timeoutMap[unit];

    if (unit !== 'seconds') {
      setSyncedPeriod(getSyncedPeriod());
    }

    const interval = setInterval(() => {
      setSyncedPeriod(getSyncedPeriod());
    }, intervalTimeout);

    return () => clearInterval(interval);
  }, [lastSyncCompletedAt]);

  return (
    <>
      {lastSyncCompletedAt && (
        <div css={styles.wrapper}>
          <div css={styles.syncedAtIcon}>
            <Icon name='sync' height={16} width={16} />
          </div>
          <div>
            <Text size='small' css={styles.syncedAtText}>
              {syncedPeriod}
            </Text>
          </div>
        </div>
      )}
    </>
  );
};
